/**
 * Store Order Supplies Form Component
 * 
 * Matches the store order form style with autopopulated customer information
 * and card-based product selection matching the CSV structure.
 * 
 * @module StoreOrderSuppliesForm
 */

import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  MapPin, 
  Package, 
  ShoppingCart, 
  Search, 
  Filter, 
  Star,
  CheckCircle,
  AlertCircle,
  Plus,
  Minus,
  Calendar,
  Mail,
  Phone
} from 'lucide-react';
import {
  OptimizedFormContainer,
  OptimizedFormSection,
  OptimizedFormGrid,
  OptimizedProductGrid,
  OptimizedProductCard,
  OptimizedOrderSummary,
  OptimizedSubmitButton,
  SearchAndFilterContainer,
  SearchInput,
  FilterSelect,
  CollapsibleSection,
  CategoryBadge,
  SectionTitle,
  FieldContainer,
  QuantityControl,
  SuccessMessage,
  ErrorMessage,
  LoadingSpinner,
  GlobalOrderSummary
} from './shared/OptimizedFormComponents';
import {
  CustomerInformationSection,
  OrderInformationSection,
  SharedOrderFormData,
  getMockCustomerData
} from './shared/SharedFormSections';
import { PreviewOrderButton } from './shared/PreviewOrderButton';
import { useCustomerData } from './shared/CustomerDataService';
import { useGlobalOrderData } from '../../hooks/useGlobalOrderData';
import { ProductCard } from './shared/ProductCard';
import { ThemeColors, Spacing, Typography, BorderRadius } from "../../styles/ThemeStyles";
import { useSupplies } from '../../hooks/useSupplies';
import { Supply, getSupplyUnitPriceForOrder } from '../../services/SupplyService';
import { BackToTopButton } from './shared/BackToTopButton';

interface SupplyItem {
  id: string;
  packetDescription: string;
  code: string;
  quantity: number;
  price: number;
  category: string; // Category will come from database
}

interface OrderFormData extends SharedOrderFormData {
  supplyItems?: SupplyItem[];
}

// Supplies will be loaded from Firestore

const SupplyFormContainer = styled(OptimizedFormContainer)`
  max-width: 1400px;
`;

const SupplyFormSection = styled(OptimizedFormSection)`
  margin-bottom: ${Spacing.xl};
`;

const ProductCategorySection = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.lg};
  padding: ${Spacing.md};
  background: ${ThemeColors.primary.light};
  border-radius: ${Spacing.sm};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${ThemeColors.primary.main};
    color: white;
  }
  
  .category-info {
    display: flex;
    align-items: center;
    gap: ${Spacing.md};
    
    .category-title {
      font-size: ${Typography.fontSize.lg};
      font-weight: ${Typography.fontWeight.bold};
      color: ${ThemeColors.text.primary};
    }
    
    .category-count {
      font-size: ${Typography.fontSize.sm};
      color: ${ThemeColors.text.secondary};
    }
  }
  
  .category-toggle {
    display: flex;
    align-items: center;
    gap: ${Spacing.sm};
    font-size: ${Typography.fontSize.sm};
    color: ${ThemeColors.text.secondary};
  }
`;

const CategoryNote = styled.div`
  background: ${ThemeColors.info.light};
  color: ${ThemeColors.info.main};
  padding: ${Spacing.sm};
  border-radius: ${Spacing.sm};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  margin-bottom: ${Spacing.md};
  text-align: center;
`;


interface StoreOrderSuppliesFormProps {
  storeId?: string;
  onShowPreview?: () => void;
}

export const StoreOrderSuppliesForm: React.FC<StoreOrderSuppliesFormProps> = ({ 
  storeId, 
  onShowPreview 
}) => {
  const { t } = useTranslation();
  const { customerData, loading: customerLoading, error: customerError } = useCustomerData(storeId || '');
  const { orderData, updateOrderData } = useGlobalOrderData();
  
  // Load supplies from Firestore
  const { supplies, loading: suppliesLoading, error: suppliesError } = useSupplies();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  // Sync customer data with global order data when customer data is loaded
  useEffect(() => {
    if (customerData) {
      // ONLY sync customer info fields, don't overwrite order info fields (poNumber, specialInstructions, etc.)
      const customerInfo = {
        customerNumber: customerData.customerNumber,
        storeName: customerData.storeName,
        address: customerData.address,
        city: customerData.city,
        province: customerData.province,
        postalCode: customerData.postalCode,
        email: customerData.email,
        phoneNumber: customerData.phoneNumber
      };
      updateOrderData(customerInfo);
    }
  }, [customerData, updateOrderData]);

  // Sync selected items with global order data (similar to other order forms)
  useEffect(() => {
    if (orderData.supplyItems === undefined) {
      return;
    }

    // Build quantities map from orderData.supplyItems using document id (not code)
    const quantitiesMap: Record<string, number> = {};
    orderData.supplyItems.forEach(item => {
      quantitiesMap[item.id] = item.quantity;
    });

    // Only update if there's a difference to avoid loops
    const currentQuantitiesStr = JSON.stringify(quantities);
    const newQuantitiesStr = JSON.stringify(quantitiesMap);
    
    if (currentQuantitiesStr !== newQuantitiesStr) {
      setQuantities(quantitiesMap);
    }
  }, [orderData.supplyItems, quantities]);

  // When Firestore supplies load or retail values change, keep line item unit prices in sync
  useEffect(() => {
    if (!supplies.length) return;
    const items = orderData.supplyItems;
    if (!items?.length) return;
    let changed = false;
    const next = items.map((item) => {
      const supply = supplies.find((s) => s.id === item.id);
      if (!supply) return item;
      const p = getSupplyUnitPriceForOrder(supply);
      if (p !== item.price) {
        changed = true;
        return { ...item, price: p };
      }
      return item;
    });
    if (changed) {
      updateOrderData({ supplyItems: next });
    }
  }, [supplies, orderData.supplyItems, updateOrderData]);

  const handleInputChange = (field: string, value: string | boolean) => {
    updateOrderData({ [field]: value });
  };

  const handleQuantityChange = (supplyId: string, quantity: number) => {
    setQuantities(prev => ({
      ...prev,
      [supplyId]: quantity
    }));

    const currentItems = orderData.supplyItems || [];
    
    if (quantity > 0) {
      // Find supply by document id (not code)
      const supply = supplies.find(s => s.id === supplyId);
      const existingItemIndex = currentItems.findIndex(item => item.id === supplyId);
      const unitPrice = supply ? getSupplyUnitPriceForOrder(supply) : 0;

      if (supply && existingItemIndex >= 0) {
        // Update existing item quantity
        const updatedItems = [...currentItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity,
          price: unitPrice
        };
        updateOrderData({ supplyItems: updatedItems });
      } else if (supply) {
        // Create new item using document id (unique identifier)
        const newItem: SupplyItem = {
          id: supply.id || supplyId, // Use document id as unique identifier
          packetDescription: supply.description || supply.descriptionEN || '',
          code: supply.ItemCode || supply.code || '', // Use ItemCode from database, fallback to code
          quantity,
          price: unitPrice,
          category: supply.category || ''
        };

        updateOrderData({ supplyItems: [...currentItems, newItem] });
      }
    } else {
      const existingItemIndex = currentItems.findIndex(item => item.id === supplyId);
      if (existingItemIndex >= 0) {
        const updatedItems = currentItems.filter((_, index) => index !== existingItemIndex);
        updateOrderData({ supplyItems: updatedItems });
      }
    }
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Group supplies by category
  const suppliesByCategory = useMemo(() => {
    const grouped: Record<string, { supplies: Supply[]; note?: string; title: string }> = {};
    
    supplies.forEach(supply => {
      const categoryKey = supply.category || 'uncategorized';
      const categoryTitle = supply.categoryEN || supply.category || 'Uncategorized';
      
      if (!grouped[categoryKey]) {
        grouped[categoryKey] = {
          supplies: [],
          title: categoryTitle,
          note: supply.note
        };
      }
      
      grouped[categoryKey].supplies.push(supply);
    });
    
    // Sort supplies within each category by code
    Object.keys(grouped).forEach(key => {
      grouped[key].supplies.sort((a, b) => a.code.localeCompare(b.code));
    });
    
    return grouped;
  }, [supplies]);

  // Filter supplies based on search and category selection
  const filteredSupplies = useMemo(() => {
    let filtered = supplies;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(supply => supply.category === selectedCategory);
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(supply =>
        (supply.description || supply.descriptionEN || '').toLowerCase().includes(searchLower) ||
        (supply.descriptionFR || '').toLowerCase().includes(searchLower) ||
        (supply.ItemCode || supply.code || '').toLowerCase().includes(searchLower) ||
        (supply.category || supply.categoryEN || '').toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [supplies, selectedCategory, searchTerm]);

  // Group filtered supplies by category
  const groupedProducts = useMemo(() => {
    const grouped: Record<string, Supply[]> = {};
    
    filteredSupplies.forEach(supply => {
      const categoryKey = supply.categoryEN || supply.category || 'Uncategorized';
      if (!grouped[categoryKey]) {
        grouped[categoryKey] = [];
      }
      grouped[categoryKey].push(supply);
    });
    
    // Sort supplies within each category by code
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => a.code.localeCompare(b.code));
    });
    
    return grouped;
  }, [filteredSupplies]);

  // Get unique categories for filter dropdown
  const categoryOptions = useMemo(() => {
    const categories = new Set<string>();
    supplies.forEach(supply => {
      if (supply.category) {
        categories.add(supply.category);
      }
    });
    
    return [
      { value: 'all', label: t('forms.allCategories', 'All Categories') },
      ...Array.from(categories).sort().map(cat => {
        const supply = supplies.find(s => s.category === cat);
        return {
          value: cat,
          label: supply?.categoryEN || cat
        };
      })
    ];
  }, [supplies, t]);

  const calculateTotal = () => {
    const items = orderData.supplyItems || [];
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <SupplyFormContainer>
      {/* Customer Information */}
      <CustomerInformationSection
        data={orderData}
        onChange={handleInputChange}
        errors={{}}
        loading={customerLoading}
        error={customerError}
      />

      {/* Order Information - Use shared component like Store Order forms (no cust order minimum notice for supplies) */}
      <OrderInformationSection
        data={orderData}
        onChange={handleInputChange}
        errors={{}}
        showCustOrderMinimumNotice={false}
        />
        
        {/* Special Note for Order Supplies */}
      <OptimizedFormSection>
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: BorderRadius.sm,
          padding: Spacing.sm,
          marginBottom: Spacing.md,
          textAlign: 'center',
          fontSize: Typography.fontSize.sm,
          fontWeight: Typography.fontWeight.medium,
          color: '#856404'
        }}>
          {t('forms.noMetalRequired', '**NO METAL REQUIRED/ PAS DE METAL**')}
        </div>
      </OptimizedFormSection>

      {/* Product Selection */}
      <SupplyFormSection>
        <SectionTitle 
          icon={Package}
          title={t('forms.productSelection', 'Product Selection / Sélection de produits')}
        />

        <SearchAndFilterContainer>
          <input
            type="text"
            placeholder={t('forms.searchProducts', 'Search products...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: Spacing.sm,
              border: `1px solid ${ThemeColors.border.light}`,
              borderRadius: BorderRadius.sm,
              fontSize: Typography.fontSize.sm,
              flex: 1
            }}
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: Spacing.sm,
              border: `1px solid ${ThemeColors.border.light}`,
              borderRadius: BorderRadius.sm,
              fontSize: Typography.fontSize.sm,
              backgroundColor: 'white'
            }}
          >
            {categoryOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </SearchAndFilterContainer>

        {suppliesLoading ? (
          <LoadingSpinner>
            <div>{t('forms.loadingProducts', 'Loading products...')}</div>
          </LoadingSpinner>
        ) : suppliesError ? (
          <ErrorMessage>
            {suppliesError}
          </ErrorMessage>
        ) : Object.keys(groupedProducts).length === 0 ? (
          <ErrorMessage>
            {t('forms.noProductsFound', 'No supplies found. Please ensure supplies are uploaded to the database.')}
          </ErrorMessage>
        ) : (
          Object.entries(groupedProducts).map(([categoryKey, categorySupplies]) => {
          const sectionId = `category-${categoryKey}`;
          const isCollapsed = collapsedSections[sectionId];
            const categoryInfo = suppliesByCategory[categorySupplies[0]?.category || ''];
          
          return (
            <ProductCategorySection key={categoryKey}>
              <CategoryHeader onClick={() => toggleSection(sectionId)}>
                <div className="category-info">
                  <div className="category-title">
                      {categoryKey}
                  </div>
                  <div className="category-count">
                      {categorySupplies.length} {categorySupplies.length === 1 ? t('common.item') : t('common.items')}
                  </div>
                </div>
                <div className="category-toggle">
                    <span>{isCollapsed ? t('forms.show', 'Show') : t('forms.hide', 'Hide')}</span>
                  {isCollapsed ? <Plus size={16} /> : <Minus size={16} />}
                </div>
              </CategoryHeader>
              
                {categoryInfo?.note && (
                <CategoryNote>
                    {categoryInfo.note}
                </CategoryNote>
              )}
              
              {!isCollapsed && (
                <OptimizedProductGrid>
                    {categorySupplies.map((supply) => {
                      const description = supply.description || supply.descriptionEN || '';
                      const supplyId = supply.id || supply.code; // Use document id as unique identifier
                      const unitPrice = getSupplyUnitPriceForOrder(supply);
                      return (
                    <ProductCard
                          key={supplyId}
                      product={{
                            id: supplyId, // Use document id (unique)
                            code: supply.ItemCode || supply.code || '', // Use ItemCode from database, fallback to code
                            name: description,
                            price: unitPrice > 0 ? unitPrice : undefined,
                            retail: supply.retail || '',
                            category: supply.category || ''
                      }}
                          quantity={quantities[supplyId] || 0}
                      onQuantityChange={(productId, quantity) => handleQuantityChange(productId, quantity)}
                          isSelected={(quantities[supplyId] || 0) > 0}
                      showPrice={unitPrice > 0}
                      emphasizePriceInRed={unitPrice > 0}
                      showCategory={false}
                      isOutOfStock={orderData.orderType === 'custOrder' && (supply.status || 'available') === 'out-of-stock'}
                    />
                      );
                    })}
                </OptimizedProductGrid>
              )}
            </ProductCategorySection>
          );
          })
        )}
      </SupplyFormSection>

      {/* Global Order Summary - Supplies Only */}
      <GlobalOrderSummary orderType="supplies" />
          
      {/* Preview Order Button with Validation - Supplies Only */}
      <PreviewOrderButton 
        orderType="supplies"
        onPreview={() => {
          if (onShowPreview) {
            onShowPreview();
          } else {
            console.log('Preview functionality not available');
          }
        }}
      />
      
      {/* Back to Top Button */}
      <BackToTopButton scrollThreshold={300} />
    </SupplyFormContainer>
  );
};

export default StoreOrderSuppliesForm;
